import { useState } from "react";
import { HubAdminDemoLayout } from "@/layout/HubAdminDemoLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";

const MOCK_ANNOUNCEMENTS = [
  {
    id: 1,
    title: "U13 Phase 2 Selection Results",
    date: "2026-06-27",
    audience: "All U13 Parents",
    category: "selection",
    content: "Phase 2 selections for U13 are complete. Selected players and reserves have been notified. Full scorecards are available in the Reports section of this portal.",
  },
  {
    id: 2,
    title: "U11 Game Schedule — Week of June 30",
    date: "2026-06-25",
    audience: "U11 Teams",
    category: "schedule",
    content: "Game 1: Saturday 9AM at Frisco Cricket Ground. Game 2: Sunday 2PM at McKinney Sports Complex. Please arrive 30 minutes early for warmups.",
  },
  {
    id: 3,
    title: "Payment Deadline Reminder",
    date: "2026-06-20",
    audience: "All Academies",
    category: "payment",
    content: "Phase 2 registration fees are due by June 30. Contact your academy admin if you have questions.",
  },
  {
    id: 4,
    title: "Yo-Yo Fitness Test — U15 Mandatory",
    date: "2026-06-15",
    audience: "U15 Players",
    category: "eligibility",
    content: "All U15 players must complete the Yo-Yo fitness test before July 5 to remain eligible for Regional selection. Results will be posted here.",
  },
  {
    id: 5,
    title: "Welcome to Dallas Hub Portal",
    date: "2026-06-01",
    audience: "All Members",
    category: "general",
    content: "The Dallas Hub portal is now live in demo mode. Selection, tournament, payment, and reporting features are available for preview.",
  },
];

type Category = "all" | "selection" | "schedule" | "payment" | "eligibility" | "general";

const FILTER_PILLS: { label: string; value: Category }[] = [
  { label: "All", value: "all" },
  { label: "Selection", value: "selection" },
  { label: "Schedule", value: "schedule" },
  { label: "Payment", value: "payment" },
  { label: "Eligibility", value: "eligibility" },
  { label: "General", value: "general" },
];

const CATEGORY_BORDER: Record<string, string> = {
  selection:   "border-l-green-500",
  schedule:    "border-l-blue-500",
  payment:     "border-l-amber-500",
  eligibility: "border-l-red-500",
  general:     "border-l-gray-400",
};

const CATEGORY_BADGE: Record<string, string> = {
  selection:   "bg-green-50 text-green-700 border-green-200",
  schedule:    "bg-blue-50 text-blue-700 border-blue-200",
  payment:     "bg-amber-50 text-amber-700 border-amber-200",
  eligibility: "bg-red-50 text-red-700 border-red-200",
  general:     "bg-gray-50 text-gray-600 border-gray-200",
};

type Announcement = typeof MOCK_ANNOUNCEMENTS[0];

const AUDIENCE_OPTIONS = [
  "All Members", "All Academies", "All Parents",
  "U11 Teams", "U11 Players", "All U11 Parents",
  "U13 Teams", "U13 Players", "All U13 Parents",
  "U15 Teams", "U15 Players", "All U15 Parents",
  "U17 Teams", "U17 Players", "All U17 Parents",
];

export default function HubAnnouncementsDemo() {
  const { toast } = useToast();
  const [announcements, setAnnouncements] = useState<Announcement[]>(MOCK_ANNOUNCEMENTS);
  const [activeFilter, setActiveFilter] = useState<Category>("all");
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [read, setRead] = useState<Set<number>>(new Set());
  const [composeOpen, setComposeOpen] = useState(false);

  // Compose form state
  const [composeTitle, setComposeTitle] = useState("");
  const [composeCategory, setComposeCategory] = useState<string>("general");
  const [composeAudience, setComposeAudience] = useState("All Members");
  const [composeContent, setComposeContent] = useState("");

  function toggleExpand(id: number) {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function markRead(id: number) {
    setRead((prev) => new Set(prev).add(id));
  }

  function handlePost() {
    if (!composeTitle.trim() || !composeContent.trim()) return;
    const today = new Date().toISOString().split("T")[0];
    const newAnn: Announcement = {
      id: Date.now(),
      title: composeTitle.trim(),
      date: today,
      audience: composeAudience,
      category: composeCategory as Category,
      content: composeContent.trim(),
    };
    setAnnouncements((prev) => [newAnn, ...prev]);
    setComposeOpen(false);
    setComposeTitle(""); setComposeContent(""); setComposeCategory("general"); setComposeAudience("All Members");
    toast({ title: "Announcement posted", description: `"${newAnn.title}" sent to ${newAnn.audience}.` });
  }

  const filtered = activeFilter === "all"
    ? announcements
    : announcements.filter((a) => a.category === activeFilter);

  return (
    <HubAdminDemoLayout>
      <div className="px-4 md:px-8 py-6 space-y-5" data-testid="hub-announcements-page">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-bold text-gray-900">Announcements</h1>
            <Badge className="bg-amber-100 text-amber-800 border-amber-300 border text-xs">DEMO</Badge>
          </div>
          <Button
            size="sm"
            className="bg-[#1E2761] hover:bg-[#2d3a8c] text-white"
            onClick={() => setComposeOpen(true)}
            data-testid="btn-compose"
          >
            + Compose Announcement
          </Button>
        </div>

        {/* Filter pills */}
        <div className="flex flex-wrap gap-2" data-testid="category-filter-pills">
          {FILTER_PILLS.map(({ label, value }) => (
            <button
              key={value}
              onClick={() => setActiveFilter(value)}
              data-testid={`filter-pill-${value}`}
              className={`px-3 py-1 rounded-full text-sm font-medium border transition-colors ${
                activeFilter === value
                  ? "bg-[#1E2761] text-white border-[#1E2761]"
                  : "bg-white text-gray-600 border-gray-300 hover:border-gray-400"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Announcement cards */}
        <div className="space-y-3" data-testid="announcements-list">
          {filtered.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-10">No announcements in this category.</p>
          )}
          {filtered.map((ann) => {
            const isExpanded = expanded.has(ann.id);
            const isRead = read.has(ann.id);
            const borderColor = CATEGORY_BORDER[ann.category] ?? "border-l-gray-300";
            const badgeCls = CATEGORY_BADGE[ann.category] ?? "bg-gray-50 text-gray-600 border-gray-200";

            return (
              <div
                key={ann.id}
                className={`bg-white rounded-xl border border-gray-200 border-l-4 ${borderColor} overflow-hidden transition-opacity ${isRead ? "opacity-60" : ""}`}
                data-testid={`announcement-card-${ann.id}`}
              >
                <button
                  className="w-full px-5 py-4 text-left space-y-2"
                  onClick={() => toggleExpand(ann.id)}
                  aria-expanded={isExpanded}
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <p className="font-semibold text-gray-900 text-sm">{ann.title}</p>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{ann.date}</span>
                      <Badge className={`border text-xs ${badgeCls}`}>{ann.category}</Badge>
                    </div>
                  </div>
                  <p className="text-xs text-blue-600 font-medium">To: {ann.audience}</p>
                  {!isExpanded && (
                    <p className="text-sm text-gray-500">
                      {ann.content.length > 100 ? ann.content.slice(0, 100) + "..." : ann.content}
                    </p>
                  )}
                </button>

                {isExpanded && (
                  <div className="px-5 pb-4 space-y-3">
                    <p className="text-sm text-gray-700 leading-relaxed">{ann.content}</p>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-xs h-7"
                      onClick={() => markRead(ann.id)}
                      data-testid={`btn-mark-read-${ann.id}`}
                    >
                      {isRead ? "✓ Marked as Read" : "Mark as Read"}
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Compose Announcement Dialog */}
      <Dialog open={composeOpen} onOpenChange={setComposeOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Compose Announcement</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Title */}
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-700">Title</label>
              <input
                type="text"
                value={composeTitle}
                onChange={(e) => setComposeTitle(e.target.value)}
                placeholder="e.g. U13 Game Schedule — July 5"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E2761]"
              />
            </div>

            {/* Category + Audience */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-xs font-medium text-gray-700">Category</label>
                <select
                  value={composeCategory}
                  onChange={(e) => setComposeCategory(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E2761]"
                >
                  <option value="selection">Selection</option>
                  <option value="schedule">Schedule</option>
                  <option value="payment">Payment</option>
                  <option value="eligibility">Eligibility</option>
                  <option value="general">General</option>
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-gray-700">Send To</label>
                <select
                  value={composeAudience}
                  onChange={(e) => setComposeAudience(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E2761]"
                >
                  {AUDIENCE_OPTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
            </div>

            {/* Message */}
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-700">Message</label>
              <textarea
                value={composeContent}
                onChange={(e) => setComposeContent(e.target.value)}
                rows={5}
                placeholder="Type your announcement here..."
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E2761] resize-none"
              />
            </div>

            <div className="flex gap-2 justify-end pt-1">
              <Button variant="outline" size="sm" onClick={() => setComposeOpen(false)}>Cancel</Button>
              <Button
                size="sm"
                className="bg-[#1E2761] hover:bg-[#2d3a8c] text-white"
                disabled={!composeTitle.trim() || !composeContent.trim()}
                onClick={handlePost}
              >
                Post Announcement
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </HubAdminDemoLayout>
  );
}
